import type { components } from '../../api/schema'
import { normalizeDmpChatSessionFile, type DmpChatSessionFile } from './dmpChatSessionFiles.ts'

export type DmpChatComposerAgentConfig = Pick<
  components['schemas']['Messages.UserAgent.UserAgentConfig'],
  'skillKeys' | 'tools' | 'toolsetKeys'
>

export type DmpChatSkillCatalogEntry = Pick<
  components['schemas']['Messages.Skill.SkillSummary'],
  'key' | 'name' | 'description'
>

export type DmpChatToolCatalogEntry = Pick<
  components['schemas']['Messages.Agent.ExistingRegisteredTool'],
  'functionName' | 'name' | 'description'
>

export type DmpChatToolsetCatalogEntry = Pick<
  components['schemas']['Messages.Agent.ExistingRegisteredToolset'],
  'key' | 'name' | 'description'
>

export type DmpChatMentionType = 'file' | 'skill' | 'tool' | 'toolset'

export interface DmpChatMentionItem {
  readonly id: string
  readonly type: DmpChatMentionType
  readonly label: string
  readonly description?: string
  readonly icon: DmpChatMentionType
}

export interface DmpChatMentionCategory {
  readonly id: 'files' | 'skills' | 'tools'
  readonly label: string
  readonly items: readonly DmpChatMentionItem[]
}

export interface BuildDmpChatMentionCategoriesOptions {
  readonly sessionFiles: readonly DmpChatSessionFile[]
  readonly agentConfig?: DmpChatComposerAgentConfig | null
  readonly skills?: readonly DmpChatSkillCatalogEntry[]
  readonly tools?: readonly DmpChatToolCatalogEntry[]
  readonly toolsets?: readonly DmpChatToolsetCatalogEntry[]
}

export const DMP_CHAT_SLASH_COMMANDS = [
  {
    id: 'help',
    label: '/help',
    description: 'Show the available composer commands.',
  },
  {
    id: 'new',
    label: '/new',
    description: 'Start a new chat.',
  },
  {
    id: 'files',
    label: '/files',
    description: 'Open the session Files tab.',
  },
] as const

export type DmpChatSlashCommandId = (typeof DMP_CHAT_SLASH_COMMANDS)[number]['id']
export type DmpChatSlashIntent = 'help' | 'new-chat' | 'focus-files'

const DMP_CHAT_SLASH_INTENTS: Record<DmpChatSlashCommandId, DmpChatSlashIntent> = {
  help: 'help',
  new: 'new-chat',
  files: 'focus-files',
}

export function buildDmpChatMentionCategories({
  sessionFiles,
  agentConfig,
  skills = [],
  tools = [],
  toolsets = [],
}: BuildDmpChatMentionCategoriesOptions): readonly DmpChatMentionCategory[] {
  return [
    {
      id: 'files',
      label: 'Files',
      items: sessionFiles.flatMap((file) => {
        const mention = createFileMention(file)
        return mention ? [mention] : []
      }),
    },
    {
      id: 'skills',
      label: 'Skills',
      items: uniqueNonEmptyValues(agentConfig?.skillKeys).map((key) => {
        const catalogEntry = skills.find((skill) => skill.key?.trim() === key)
        return createMentionItem(key, 'skill', normalizeText(catalogEntry?.name) ?? key, catalogEntry?.description)
      }),
    },
    {
      id: 'tools',
      label: 'Tools',
      items: [
        ...uniqueNonEmptyValues(agentConfig?.tools).map((key) => {
          const catalogEntry = tools.find((tool) => tool.functionName?.trim() === key)
          return createMentionItem(key, 'tool', normalizeText(catalogEntry?.name) ?? key, catalogEntry?.description)
        }),
        ...uniqueNonEmptyValues(agentConfig?.toolsetKeys).map((key) => {
          const catalogEntry = toolsets.find((toolset) => toolset.key.trim() === key)
          return createMentionItem(key, 'toolset', normalizeText(catalogEntry?.name) ?? key, catalogEntry?.description)
        }),
      ],
    },
  ]
}

export function getDmpChatSlashIntent(commandId: string): DmpChatSlashIntent | null {
  const normalizedCommandId = commandId.trim().replace(/^\/+/, '')
  if (!isDmpChatSlashCommandId(normalizedCommandId)) {
    return null
  }

  return DMP_CHAT_SLASH_INTENTS[normalizedCommandId]
}

export function formatDmpChatSlashHelp(): string {
  return DMP_CHAT_SLASH_COMMANDS.map((command) => `${command.label} — ${command.description}`).join('\n')
}

function createFileMention(file: DmpChatSessionFile): DmpChatMentionItem | null {
  const normalizedFile = normalizeDmpChatSessionFile(file)
  if (!normalizedFile?.reference) {
    return null
  }

  return createMentionItem(normalizedFile.reference, 'file', normalizedFile.fileName)
}

function createMentionItem(
  id: string,
  type: DmpChatMentionType,
  label: string,
  description: string | null | undefined = undefined
): DmpChatMentionItem {
  const normalizedDescription = normalizeText(description)
  return {
    id,
    type,
    label,
    icon: type,
    ...(normalizedDescription ? { description: normalizedDescription } : {}),
  }
}

function uniqueNonEmptyValues(values: readonly (string | null | undefined)[] | null | undefined): string[] {
  const uniqueValues = new Set<string>()
  for (const value of values ?? []) {
    const normalizedValue = value?.trim()
    if (normalizedValue) {
      uniqueValues.add(normalizedValue)
    }
  }
  return Array.from(uniqueValues)
}

function normalizeText(value: string | null | undefined): string | undefined {
  const normalizedValue = value?.trim()
  return normalizedValue || undefined
}

function isDmpChatSlashCommandId(value: string): value is DmpChatSlashCommandId {
  return value === 'help' || value === 'new' || value === 'files'
}
